import React, { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchUsers } from '../../redux/fetchUsers';
import { API_BASE_URL } from '../../config/config';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Dropdown } from 'react-bootstrap'; 

import EditUserDialog from './EditUserDialog';

// Custom Dropdown Toggle for the three-dots icon
const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
  <button
    className="btn btn-sm btn-light"
    ref={ref}
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
  >
    {children}
  </button>
));


const ManageUsers = () => {
  const dispatch = useDispatch();
  const { users, loading, error } = useSelector((state) => state.user);

  const [selectedUserId, setSelectedUserId] = useState(null);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [isEditUserDialogOpen, setEditUserDialogOpen] = useState(false);
  const [isEditPasswordDialogOpen, setEditPasswordDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });

  useEffect(() => {
    dispatch(fetchUsers());
  }, [dispatch]);

  const handleAction = (action, userId) => {
    setSelectedUserId(userId);
    const user = users.find((user) => user._id === userId);
    setSelectedUser(user);

    if (action === 'edit') setEditUserDialogOpen(true);
    if (action === 'password') setEditPasswordDialogOpen(true);
    if (action === 'delete') {
      if (window.confirm('Are you sure you want to delete this user?')) {
        handleDeleteUser(userId);
      }
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/users/${userId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      dispatch(fetchUsers());
      toast.success('User deleted successfully');
    } catch (error) {
      toast.error(`Error deleting user: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleSaveUserDetails = async (updatedData) => {
    try {
      await axios.patch(`${API_BASE_URL}/api/users/${selectedUserId}/edit`, updatedData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      dispatch(fetchUsers());
      toast.success('User details updated successfully');
      setEditUserDialogOpen(false);
    } catch (error)
    {
      toast.error(`Error updating user: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleChangePassword = async (newPassword) => {
    try {
      await axios.patch(`${API_BASE_URL}/api/users/${selectedUserId}/change-password`, { newPassword }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      toast.success('Password changed successfully');
      setEditPasswordDialogOpen(false);
    } catch (error) {
      toast.error(`Error changing password: ${error.response?.data?.message || error.message}`);
    }
  };

  const filteredUsers = useMemo(() => {
    return users.filter((user) =>
      ['name', 'userId', 'email', 'userType'].some((field) =>
        user[field]?.toString().toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  }, [users, searchQuery]);

  const sortedUsers = useMemo(() => {
    if (!sortConfig.key) return filteredUsers;
    return [...filteredUsers].sort((a, b) => {
      const aValue = a[sortConfig.key] || '';
      const bValue = b[sortConfig.key] || '';
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredUsers, sortConfig]);
  
  const totalPages = Math.ceil(sortedUsers.length / rowsPerPage);
  const paginatedUsers = sortedUsers.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  if (loading) return <div className="d-flex justify-content-center align-items-center vh-100"><div className="spinner-border" /></div>;
  if (error) return <div className="alert alert-danger">Error: {error}</div>;

  return (
    <div className="container-fluid my-4">
      <div className="card shadow-lg border-0" style={{ borderRadius: '1rem' }}>
        <div className="card-header bg-white d-flex justify-content-between align-items-center py-3">
          <h4 className="mb-0 text-primary">User Management</h4>
          <div className="input-group w-50">
            <span className="input-group-text bg-light border-0"><i className="bi bi-search"></i></span>
            <input
              type="text"
              className="form-control border-0 bg-light"
              placeholder="Search by name, ID, email, or role..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
        <div className="card-body p-0">
          {/* The 'table-responsive' div has been removed from around the table */}
          <table className="table table-hover table-striped mb-0">
            <thead className="table-light">
              <tr>
                {['name', 'userId', 'email', 'userType', 'reported_to_name', 'phone'].map((key) => (
                  <th key={key} onClick={() => handleSort(key)} style={{cursor: 'pointer'}}>
                    {key.replace(/_/g, ' ').replace(/(?:^|\s)\S/g, a => a.toUpperCase())}
                    <i className={`bi bi-arrow-${sortConfig.key === key ? (sortConfig.direction === 'asc' ? 'up' : 'down') : 'down-up'} ms-2`} />
                  </th>
                ))}
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedUsers.map((user) => (
                <tr key={user._id}>
                  <td>{user.name}</td>
                  <td>{user.userId}</td>
                  <td>{user.email}</td>
                  <td><span className={`badge bg-${user.userType === 'Admin' ? 'danger' : user.userType === 'Subadmin' ? 'warning' : 'info'}`}>{user.userType}</span></td>
                  <td>{user.reported_to_name || 'N/A'}</td>
                  <td>{user.phone}</td>
                  <td className="text-end">
                    <Dropdown>
                      <Dropdown.Toggle as={CustomToggle}>
                        <i className="bi bi-three-dots-vertical"></i>
                      </Dropdown.Toggle>
                      <Dropdown.Menu align="end">
                        <Dropdown.Item onClick={() => handleAction('edit', user._id)}>
                          <i className="bi bi-pencil-square me-2"></i>Edit
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => handleAction('password', user._id)}>
                          <i className="bi bi-key me-2"></i>Change Password
                        </Dropdown.Item>
                        <Dropdown.Divider />
                        <Dropdown.Item className="text-danger" onClick={() => handleAction('delete', user._id)}>
                          <i className="bi bi-trash me-2"></i>Delete
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="card-footer bg-white d-flex justify-content-between align-items-center py-3">
          <small>Page {page} of {totalPages}</small>
          <nav>
            <ul className="pagination mb-0">
              <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setPage(page - 1)}>«</button>
              </li>
              {[...Array(totalPages).keys()].map(p => (
                <li key={p} className={`page-item ${page === p + 1 ? 'active' : ''}`}>
                  <button className="page-link" onClick={() => setPage(p + 1)}>{p + 1}</button>
                </li>
              ))}
              <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setPage(page + 1)}>»</button>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      <EditUserDialog open={isEditUserDialogOpen} onClose={() => setEditUserDialogOpen(false)} user={selectedUser} onSave={handleSaveUserDetails} />

      <ToastContainer position="bottom-right" autoClose={3000} hideProgressBar />
    </div>
  );
};

export default ManageUsers;